import {
  Box,
  Grid,
  GridItem,
  HStack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { decode } from "js-base64";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";
import { OnProgressProps } from "react-player/base";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import AudioDescriptionManager, {
  AudioDescriptionManagerHandle,
} from "../../components/AudioDescriptionManager";
import DubbingManager, {
  DubbingManagerHandle,
} from "../../components/DubbingManager";
import HomeworkStatusChip from "../../components/HomeworkStatusChip";
import SubtitleAndCaptionManager from "../../components/SubtitleAndCaptionsManager";
import VideoPlayer, { VideoPlayerHandle } from "../../components/VideoPlayer";
import VoiceOverManager, {
  VoiceOverManagerHandle,
} from "../../components/VoiceOverManager";
import ChatContainer from "../../components/chat/ChatContainer";
import ChatInputBar from "../../components/chat/ChatInputBar";
import withConfirmOnLeave from "../../components/hoc/withConfirmOnLeave";
import HomeworkActions from "../../components/homework/HomeworkActions";
import useGetExerciseDetails from "../../hooks/api/exercise/useGetExerciseDetails";
import useGetHomeworkDetails from "../../hooks/api/homework/useGetHomeworkDetails";
import useUpdateHomeworkStatus from "../../hooks/api/homework/useUpdateHomeworkStatus";
import { ChatType } from "../../models/Chat";
import useAuthState from "../../state/auth.state";
import useHomeworkState from "../../state/homework.state";
import { getVideoUrl } from "../../utils/common";
import { vttToCues } from "../../utils/vtt";
import BasePage from "../common/BasePage";

type Params = {
  homeworkId: string;
};

const HomeworkPage = () => {
  const { t } = useTranslation();
  const { role } = useAuthState();
  const { homeworkId } = useParams<Params>();
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const isHomeworkShared = useMemo(
    () => searchParams.get("isShared") === "true",
    [searchParams],
  );
  const _videoRef = useRef<VideoPlayerHandle>(null);
  const voiceOverManagerRef = useRef<VoiceOverManagerHandle>(null);
  const dubbingManagerRef = useRef<DubbingManagerHandle>(null);
  const audioDescriptionManagerRef =
    useRef<AudioDescriptionManagerHandle>(null);
  const [selectedTab, setSelectedTab] = useState<"exercise" | ChatType>(
    "exercise",
  );
  const [videoRef, setVideoRef] = useState<ReactPlayer | null>();
  const {
    setSubtitles,
    setCaptions,
    setCategories,
    setVoiceOvers,
    setDubbings,
    setAudioDescriptions,
  } = useHomeworkState();
  const {
    data: exercise,
    isLoading: isExerciseLoading,
    isError: exerciseError,
    refetch: refetchExercise,
  } = useGetExerciseDetails(searchParams.get("exerciseId") ?? "");
  const {
    data: homework,
    isLoading: isHomeworkLoading,
    isError: homeworkError,
    refetch: refetchHomework,
  } = useGetHomeworkDetails(homeworkId ?? "", isHomeworkShared);
  const { mutateAsync: updateHomeworkStatus } = useUpdateHomeworkStatus(
    homeworkId ?? "",
  );

  const isDisabled = useMemo(
    () =>
      role === "ROLE_TEACHER" ||
      (role === "ROLE_STUDENT" && homework?.status === "APPROVED") ||
      isHomeworkShared,
    [homework?.status, isHomeworkShared, role],
  );

  useEffect(() => {
    if (homework?.status === "REJECTED" && role === "ROLE_STUDENT") {
      updateHomeworkStatus({ status: { status: "STARTED" } });
    }
  }, [homework?.status, role, updateHomeworkStatus]);

  useEffect(() => {
    if (homework) {
      const retrievedSubtitles = vttToCues(
        decode(homework.subtitleVtt ?? ""),
        "subtitles",
      );
      const retrievedCaptions = vttToCues(
        decode(homework.captionVtt ?? ""),
        "captions",
      );
      setSubtitles(() => retrievedSubtitles);
      setCaptions(() => retrievedCaptions);
      setDubbings(homework.dubbings ?? []);
      setVoiceOvers(homework.voiceOvers ?? []);
      setAudioDescriptions(homework.audioDescriptions ?? []);
      setCategories(homework.categories ?? {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homework]);

  const onReady = useCallback(() => {
    setVideoRef(_videoRef.current?.videoPlayer);
  }, [setVideoRef]);

  const refetchAll = useCallback(() => {
    return Promise.all([refetchExercise, refetchHomework]);
  }, [refetchHomework, refetchExercise]);

  const onPlay = useCallback(() => {
    voiceOverManagerRef.current?.onPlay();
    dubbingManagerRef.current?.onPlay();
    audioDescriptionManagerRef.current?.onPlay();
  }, []);

  const onPause = useCallback(() => {
    voiceOverManagerRef.current?.onPause();
    dubbingManagerRef.current?.onPause();
    audioDescriptionManagerRef.current?.onPause();
  }, []);

  const onSeek = useCallback(() => {
    voiceOverManagerRef.current?.onSeek();
    dubbingManagerRef.current?.onSeek();
    audioDescriptionManagerRef.current?.onSeek();
  }, []);

  const onProgress = useCallback((progress: OnProgressProps) => {
    voiceOverManagerRef.current?.onProgress(progress);
    dubbingManagerRef.current?.onProgress(progress);
    audioDescriptionManagerRef.current?.onProgress(progress);
  }, []);

  const onTabChange = useCallback((tabIndex: number) => {
    if (tabIndex === 0) setSelectedTab("exercise");
    if (tabIndex === 1) setSelectedTab("chat-with-teacher");
    if (tabIndex === 2) setSelectedTab("chat-with-students");
  }, []);

  const bottomRightContent = useMemo(
    () =>
      selectedTab === "exercise" ? (
        <HomeworkActions
          homework={homework!}
          refetchHomework={refetchHomework}
        />
      ) : (
        <ChatInputBar chatType={selectedTab} />
      ),
    [homework, refetchHomework, selectedTab],
  );

  return (
    <BasePage
      maxWidth="100%"
      bottomRightContent={bottomRightContent}
      isLoading={isExerciseLoading || isHomeworkLoading}
      hasError={exerciseError || homeworkError}
      retry={refetchAll}
      withBreadcrumb={false}
      bottomLeftContainerProps={
        selectedTab !== "exercise" ? { hidden: true } : {}
      }
      bottomRightContainerProps={
        selectedTab !== "exercise" ? { flexGrow: 1 } : {}
      }
    >
      <HStack justifyContent="space-between" my={4} mx={8}>
        <Box>
          {isHomeworkShared && state?.author && (
            <Text mb={2} fontSize={20} fontWeight="bold">
              {state.author}
            </Text>
          )}
          <Text>
            <strong>{exercise?.name}</strong>: {exercise?.description}
          </Text>
        </Box>
        {homework?.status && <HomeworkStatusChip status={homework.status} />}
      </HStack>
      <Tabs colorScheme="red" isFitted onChange={onTabChange}>
        <TabList>
          <Tab>{t("homework_page.exercise")}</Tab>
          <Tab isDisabled={isHomeworkShared}>
            {role === "ROLE_STUDENT"
              ? t("homework_page.teacher_chat")
              : t("homework_page.student_chat")}
          </Tab>
          <Tab>{t("homework_page.group_chat")}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <Grid
              mt={12}
              alignItems="flex-start"
              gap={12}
              templateColumns="repeat(2, 1fr)"
            >
              <GridItem colSpan={1}>
                <VideoPlayer
                  ref={_videoRef}
                  onReady={onReady}
                  url={
                    exercise?.video ? getVideoUrl(exercise.video) : undefined
                  }
                  progressInterval={100}
                  onPlay={onPlay}
                  onPause={onPause}
                  onSeek={onSeek}
                  onProgress={onProgress}
                />
              </GridItem>
              <GridItem colSpan={1}>
                {videoRef && (
                  <Tabs colorScheme="red" variant="soft-rounded" isFitted>
                    <TabList>
                      {(exercise?.hasSubtitleExercise ||
                        exercise?.hasCaptionExercise) && (
                        <Tab>{t("common.labels.subtitles_and_captions")}</Tab>
                      )}
                      {exercise?.hasDubbingExercise && (
                        <Tab>{t("common.labels.dubbing")}</Tab>
                      )}
                      {exercise?.hasVoiceOverExercise && (
                        <Tab>{t("common.labels.voice_over")}</Tab>
                      )}
                      {exercise?.hasAudioDescriptionExercise && (
                        <Tab>{t("common.labels.audio_description")}</Tab>
                      )}
                    </TabList>
                    <TabPanels>
                      {(exercise?.hasSubtitleExercise ||
                        exercise?.hasCaptionExercise) && (
                        <TabPanel>
                          <SubtitleAndCaptionManager
                            isDisabled={isDisabled}
                            hasCaptions={exercise?.hasCaptionExercise}
                            hasSubtitles={exercise?.hasSubtitleExercise}
                            videoRef={videoRef}
                          />
                        </TabPanel>
                      )}
                      {exercise?.hasDubbingExercise && (
                        <TabPanel>
                          <DubbingManager
                            isDisabled={isDisabled}
                            ref={dubbingManagerRef}
                            videoRef={videoRef}
                          />
                        </TabPanel>
                      )}
                      {exercise?.hasVoiceOverExercise && (
                        <TabPanel>
                          <VoiceOverManager
                            isDisabled={isDisabled}
                            ref={voiceOverManagerRef}
                            videoRef={videoRef}
                          />
                        </TabPanel>
                      )}
                      {exercise?.hasAudioDescriptionExercise && (
                        <TabPanel>
                          <AudioDescriptionManager
                            isDisabled={isDisabled}
                            ref={audioDescriptionManagerRef}
                            videoRef={videoRef}
                          />
                        </TabPanel>
                      )}
                    </TabPanels>
                  </Tabs>
                )}
              </GridItem>
            </Grid>
          </TabPanel>
          <TabPanel>
            {homework && !isHomeworkShared && (
              <ChatContainer
                chatType="chat-with-teacher"
                chatId={homework.id}
                isInteractive={selectedTab === "chat-with-teacher"}
              />
            )}
          </TabPanel>
          <TabPanel>
            {exercise && (
              <ChatContainer
                chatType="chat-with-students"
                chatId={`${exercise.id}`}
                homeworkStatus={homework?.status}
                isInteractive={selectedTab === "chat-with-students"}
              />
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </BasePage>
  );
};

export default withConfirmOnLeave(HomeworkPage);
